import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, filter, map, shareReplay, startWith, take, tap, finalize } from 'rxjs/operators';
import {
  Country,
  GlobalMessageService,
  GlobalMessageType,
  OccSiteAdapter,
  Region,
  RoutingService,
} from '@spartacus/core';
import { UserAccountFacade } from 'src/feature-libs/kn-br-user/kn-br-account/root/facade';
import { AtgUserManagementService } from '../atg-user-management.service';
import { AtgAddEditUsersListComponent } from '../atg-add-edit-users-list/atg-add-edit-users-list.component';

const ROLES_HIDDEN_FOR_DISTRIBUTOR_ADMIN = ['BRANCH_ADMIN', 'DISTRIBUTOR_ADMIN', 'DISTRIBUTOR_GROUP_USER'];
const ACCOUNT_STATUS_ACTIVE_EMAIL = 'ACTIVE_EMAIL';

@Component({
  selector: 'atg-add-user',
  templateUrl: './atg-add-user.component.html',
  styleUrl: './atg-add-user.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AtgAddUserComponent implements OnInit {
  isSaving = false;
  addUserForm!: FormGroup;
  selectedAccountsPayload: any[] = [];

  @ViewChild(AtgAddEditUsersListComponent)
  private accountsListRef!: AtgAddEditUsersListComponent;

accountStatusOptions: any[] = [];
  isLoadingAccountStatuses = false;

  userRoles: any[] = [];
  isLoadingUserRoles = false;
  private currentUserB2bRole: string | undefined;

  languages = [
    { value: 'en', label: 'English' },
    { value: 'fr_CA', label: 'Canadian French' },
  ];

  countries$: Observable<Country[]> | null = null;
  states$: Observable<Region[]> | null = null;
  isCountryLoading = false;
  isStateLoading = false;

  constructor(
    private fb: FormBuilder,
    private occSiteAdapter: OccSiteAdapter,
    private cdr: ChangeDetectorRef,
    private userManagementService: AtgUserManagementService,
    private routingService: RoutingService,
    private globalMessageService: GlobalMessageService,
    private userAccountFacade: UserAccountFacade,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadCountries();
    this.loadCurrentUserRoleAndUserRoles();
    this.loadAccountStatuses();
    this.subscribeToCountryChanges();
    this.subscribeToPlaceholderVisibilityChanges();
  }

  // country's own placeholder is refreshed via subscribeToCountryChanges; state/userRole
  // have no other subscriber, so on this OnPush component their custom-placeholder span
  // would otherwise stay visible after selection until some unrelated CD run caught up.
  private subscribeToPlaceholderVisibilityChanges(): void {
    ['state', 'userRole'].forEach((controlName) => {
      this.addUserForm.get(controlName)?.valueChanges.subscribe(() => this.cdr.markForCheck());
    });
  }

  private loadCurrentUserRoleAndUserRoles(): void {
    this.userAccountFacade
      .get()
      .pipe(
        filter((user): user is any => !!user),
        take(1)
      )
      .subscribe((user: any) => {
        this.currentUserB2bRole = user?.b2bUserRole;
        this.loadUserRoles();
      });
  }

  private subscribeToCountryChanges(): void {
    this.addUserForm.get('country')?.valueChanges
      .pipe(
        startWith(this.addUserForm.get('country')?.value),
        debounceTime(300)
      )
      .subscribe((countryIsoCode: string) => {
        this.onCountrySelected(countryIsoCode);
      });
  }

  private initForm(): void {
    this.addUserForm = this.fb.group({
      firstName:     ['', Validators.required],
      lastName:      ['', Validators.required],
      email:         ['', [Validators.required, Validators.email]],
      businessPhone: ['', [Validators.required, Validators.minLength(7), Validators.maxLength(10), Validators.pattern('^[0-9]*$')]],
      address:       ['', Validators.required],
      city:          ['', Validators.required],
      country:       ['', Validators.required],
      state:         ['', Validators.required],
      zipCode:       ['', Validators.required],
      accountStatus: ['', Validators.required],
      userRole:      ['', Validators.required],
      language:      ['en', Validators.required],
    });
  }

  private loadCountries(): void {
    this.isCountryLoading = true;
    this.countries$ = this.occSiteAdapter.loadCountries().pipe(
      map((countries) => countries.filter((country) => !!country.isocode)),
      tap(() => (this.isCountryLoading = false)),
      shareReplay(1)
    );
  }

  private loadUserRoles(): void {
    this.isLoadingUserRoles = true;
    this.userManagementService.getAvailableUserTypes()
      .pipe(
        finalize(() => {
          this.isLoadingUserRoles = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (userTypes: any[]) => {
          const hideRestrictedRoles = this.currentUserB2bRole === 'DISTRIBUTOR_ADMIN' || this.currentUserB2bRole === 'BRANCH_ADMIN';
          this.userRoles = userTypes
            .filter((ut) => !!ut.value && (!hideRestrictedRoles || !ROLES_HIDDEN_FOR_DISTRIBUTOR_ADMIN.includes(ut.value)))
            .map((ut) => ({
              label: this.formatUserTypeLabel(ut.value),
              value: ut.value,
            }));
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading user types:', err);
          this.userRoles = [];
        },
      });
  }

  private loadAccountStatuses(): void {
    this.isLoadingAccountStatuses = true;
    this.userManagementService.getAvailableAccountStatuses()
      .pipe(
        finalize(() => {
          this.isLoadingAccountStatuses = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (statuses: any[]) => {
          this.accountStatusOptions = statuses;
          if (!this.addUserForm.get('accountStatus')?.value && statuses.length > 0) {
            this.addUserForm.get('accountStatus')?.setValue(statuses[0].value, { emitEvent: false });
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading account statuses:', err);
          this.accountStatusOptions = [];
        },
      });
  }

  private formatUserTypeLabel(value: string): string {
    return value
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  onCountrySelected(countryIsoCode: string): void {
    this.addUserForm.get('state')?.reset('', { emitEvent: false });

    if (countryIsoCode) {
      this.isStateLoading = true;
      this.states$ = this.occSiteAdapter.loadRegions(countryIsoCode).pipe(
        map((regions) => regions.filter((region) => !!region.isocode)),
        tap(() => {
          this.isStateLoading = false;
          this.cdr.markForCheck();
        }),
        shareReplay(1)
      );
    } else {
      this.states$ = null;
    }
    // This runs from a debounced (async) callback on an OnPush component;
    // without this, the view never notices states$ changed, so the async pipe
    // never subscribes and loadRegions() is never actually triggered.
    this.cdr.markForCheck();
  }

  onAccountsPayloadChange(payload: any[]): void {
    this.selectedAccountsPayload = payload ?? [];
    this.cdr.markForCheck();
  }

  saveUser(): void {
    this.addUserForm.markAllAsTouched();

    if (this.addUserForm.invalid || this.isSaving) {
      this.cdr.markForCheck();
      return;
    }

    const v = this.addUserForm.value;

    if (v.accountStatus === ACCOUNT_STATUS_ACTIVE_EMAIL && !(this.accountsListRef?.getPayload() ?? []).length) {
      this.globalMessageService.add(
        { key: 'atgUserManagement.addUser.accountSelectionRequired' },
        GlobalMessageType.MSG_TYPE_ERROR
      );
      this.cdr.markForCheck();
      return;
    }

    const rawStateValue =
      typeof v.state === 'string'
        ? v.state
        : v.state?.isocodeShort || v.state?.isocode || v.state?.name || '';
    const stateValue = this.normalizeStateCode(rawStateValue);

    const payload = {
      name:          `${v.firstName ?? ''} ${v.lastName ?? ''}`.trim(),
      email:         v.email,
      businessPhone: v.businessPhone,
      address:       v.address,
      city:          v.city,
      zipCode:       v.zipCode,
      accountStatus: v.accountStatus,
      userRole:      v.userRole,
      language:      v.language,
      country:       v.country,
      state:         stateValue,
      parentSoldToList: this.accountsListRef?.getPayload() ?? [],
    };

    this.isSaving = true;
    this.cdr.markForCheck();

    this.userManagementService.addUser(payload).subscribe({
      next: (res) => {
        this.isSaving = false;
        this.globalMessageService.add(
          res?.response ?? { key: 'atgUserManagement.addUser.createSuccess' },
          GlobalMessageType.MSG_TYPE_CONFIRMATION
        );
        this.cdr.markForCheck();
        setTimeout(() => this.routingService.go(['/users']), 2000);
      },
      error: (err) => {
        this.isSaving = false;
        this.globalMessageService.add(
          err?.error?.response ?? { key: 'atgUserManagement.addUser.createFailed' },
          GlobalMessageType.MSG_TYPE_ERROR
        );
        this.cdr.markForCheck();
      },
    });
  }

  cancel(): void {
    this.routingService.go(['/users']);
  }

  private normalizeStateCode(stateValue: string): string {
    if (!stateValue) {
      return '';
    }

    const trimmed = stateValue.trim();

    // Region isocode can come as US-AL; API expects AL.
    if (trimmed.includes('-')) {
      const shortCode = trimmed.split('-').pop()?.trim();
      return shortCode || trimmed;
    }

    return trimmed;
  }
}
